import Markdown from 'markdown-to-jsx';
import React, { useEffect, useState } from 'react';

interface KnowledgeBaseResponseProps {
	ragData: {
		response: string;
		citation?: string;
		likes?: number;
		dislikes?: number;
	} | null;
	handleLikeClick: () => void;
	handleDislikeClick: () => void;
	inputQuery: string;
	onSearchResponseClose: () => void;
}

const KnowledgeBaseResponse: React.FC<KnowledgeBaseResponseProps> = ({
	ragData,
	handleLikeClick,
	handleDislikeClick,
	inputQuery,
	onSearchResponseClose,
}) => {
	const [actionTaken, setActionTaken] = useState(false);
	const [likesCount, setLikesCount] = useState(0);
	const [dislikesCount, setDislikesCount] = useState(0);

	useEffect(() => {
		if (ragData) {
			setLikesCount(ragData.likes || 0);
			setDislikesCount(ragData.dislikes || 0);
			// Reset actionTaken
			setActionTaken(false);
		}
	}, [ragData]);

	const handleLike = () => {
		if (!actionTaken) {
			handleLikeClick();
			setLikesCount(likesCount + 1);
			setActionTaken(true);
		}
	};

	const handleDislike = () => {
		if (!actionTaken) {
			handleDislikeClick();
			setDislikesCount(dislikesCount + 1);
			setActionTaken(true);
		}
	};

	return (
		<div className="">
			{ragData?.response && (
				<div className="bg-[#97EEFF] p-2 rounded-lg">
					<div>
						<div className="flex justify-between items-center mb-2 gap-2">
							<p className="text-xs font-semibold text-[#1f1f1f]">{inputQuery}</p>
							<button
								type="button"
								title="hide response"
								onClick={onSearchResponseClose}
								className="w-6 flex-none h-6 p-0 bg-[#7EDEF1] inline-flex justify-center items-center rounded-full"
							>
								<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
									<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
								</svg>
							</button>
						</div>
						<div className="text-wrap text-xs normal text-[#1f1f1f] ragResponse"><Markdown>{ragData.response.replace(/\\n"$/, '').trim()}</Markdown></div>
						{ragData?.citation && (
							<div className="mt-2 flex gap-1 justify-start items-center flex-wrap">
								<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
									<path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
								</svg>
								<span
									className="underline break-all text-xs leading-5 font-semibold"
									dangerouslySetInnerHTML={{ __html: ragData.citation }}
								/>
							</div>
						)}
					</div>
					<div className="flex gap-2 items-center justify-end mt-2">
						<button
							onClick={handleLike}
							disabled={actionTaken}
							className={`w-6 h-6 p-0 rounded-full bg-[#7EDEF1] flex justify-center items-center relative ${actionTaken && 'opacity-50 cursor-not-allowed'
								}`}
						>
							<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
								<path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z" />
							</svg>
							<span className="absolute text-[10px] font-semibold -top-3 -right-1 bg-[#CDF6FF] rounded-full min-w-5 h-4 px-1 leading-4">
								{likesCount}
							</span>
						</button>
						<button
							onClick={handleDislike}
							disabled={actionTaken}
							className={`w-6 h-6 p-0 rounded-full bg-[#7EDEF1] flex justify-center items-center relative ${actionTaken && 'opacity-50 cursor-not-allowed'
								}`}
						>
							<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
								<path strokeLinecap="round" strokeLinejoin="round" d="M7.498 15.25H4.372c-1.026 0-1.945-.694-2.054-1.715a12.137 12.137 0 0 1-.068-1.285c0-2.848.992-5.464 2.649-7.521C5.287 4.247 5.886 4 6.504 4h4.016a4.5 4.5 0 0 1 1.423.23l3.114 1.04a4.5 4.5 0 0 0 1.423.23h1.294M7.498 15.25c.618 0 .991.724.725 1.282A7.471 7.471 0 0 0 7.5 19.75 2.25 2.25 0 0 0 9.75 22a.75.75 0 0 0 .75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 0 0 2.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384m-10.253 1.5H9.7m8.075-9.75c.01.05.027.1.05.148.593 1.2.925 2.55.925 3.977 0 1.487-.36 2.89-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398-.306.774-1.086 1.227-1.918 1.227h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 0 0 .303-.54" />
							</svg>
							<span className="absolute text-[10px] font-semibold -top-3 -right-1 bg-[#CDF6FF] rounded-full min-w-5 h-4 px-1 leading-4">
								{dislikesCount}
							</span>
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default KnowledgeBaseResponse;
